import {
  create,
  type Mutate,
  type StoreApi,
  type UseBoundStore,
} from "zustand";
import {
  createJSONStorage,
  persist,
  type StateStorage,
} from "zustand/middleware";
import {
  isCatalogFilterTag,
  normalizeCatalogFilterTags,
} from "@/features/catalog/utils/tagFilterGroups";
import type { SearchScope } from "@/features/search/searchScope";
import {
  filterUuidThreadIds,
  migrateThreadIdArray,
} from "@/shared/lib/threadIdMigration";

export const CATALOG_STORE_VERSION = 7;
const CATALOG_CONTEXT_HISTORY_VERSION = 6;
export const CATALOG_LAYOUT_MIN = 1;
export const CATALOG_COLUMNS_MAX = 20;
export const CATALOG_ROWS_MAX = 40;
/** カタログ1ページあたりに取得できる最大件数 (columns × rowsの上限) */
export const CATALOG_PAGE_LIMIT_MAX = 300;
export const CATALOG_SORTS = [
  "bump",
  "new",
  "old",
  "replies",
  "momentum",
  "soudane",
] as const;
export type SortType = (typeof CATALOG_SORTS)[number];
export interface CatalogContext {
  readonly boardSlug: string;
  readonly page: number;
  readonly sort: SortType;
}
declare const CATALOG_CONTEXT_KEY: unique symbol;
export type CatalogContextKey = string & {
  readonly [CATALOG_CONTEXT_KEY]: true;
};
export type CatalogHistory = Partial<
  Record<CatalogContextKey, readonly string[]>
>;
export interface CatalogHistoryState {
  lastCatalogIds: CatalogHistory;
}
export type SortDirection = "asc" | "desc";
export type AnimSetting = "always" | "hover" | "never";
export type CatalogImageSize = 50 | 75 | 100 | 125 | 150 | 175 | 200;

export type ThreadMenuOpenMethod =
  | "auto"
  | "long-press"
  | "menu-button"
  | "off";

/** ホバープレビューのスレ画拡大率(%)の下限 */
export const CATALOG_HOVER_SCALE_MIN = 100;
/** ホバープレビューのスレ画拡大率(%)の上限 */
export const CATALOG_HOVER_SCALE_MAX = 300;
/** ホバープレビューのスレ画拡大率(%)の既定値 */
export const CATALOG_HOVER_SCALE_DEFAULT = 100;

/** カタログ本文の文字倍率(%)の下限 */
export const CATALOG_FONT_SCALE_MIN = 50;
/** カタログ本文の文字倍率(%)の上限 */
export const CATALOG_FONT_SCALE_MAX = 300;
/** カタログ本文の文字倍率(%)の既定値 */
export const CATALOG_FONT_SCALE_DEFAULT = 100;

/** カタログ本文の表示行数の下限。0は本文欄そのものを非表示にする。 */
export const CATALOG_TEXT_LINES_MIN = 0;
/** カタログ本文の表示行数の上限 */
export const CATALOG_TEXT_LINES_MAX = 3;
/** カタログ本文の表示行数の既定値 */
export const CATALOG_TEXT_LINES_DEFAULT = 1;

interface CatalogValues {
  currentSort: SortType;
  /** v2永続データとの互換用。server sortはcurrentSortだけで決まる */
  sortDirection: SortDirection;
  page: number;
  columns: number;
  rows: number;
  isLayoutInitialized: boolean;
  textLines: number;
  imageSize: CatalogImageSize;
  openInNewTab: boolean;
  showNew: boolean;
  showCount: boolean;
  showUnreadCount: boolean;
  catalogAnim: AnimSetting;
  autoReload: boolean;
  /** カタログカードのホバーでスレ画拡大と本文を表示する。既定OFF */
  catalogHoverPreview: boolean;
  /** ホバープレビューのスレ画拡大率(%) */
  catalogHoverScale: number;
  /** カタログ本文の文字倍率(%) */
  catalogFontScale: number;
  threadMenuOpenMethod: ThreadMenuOpenMethod;
  lastCatalogIds: CatalogHistory;
  searchQuery: string;
  searchScope: SearchScope;
  selectedTag: string | null;
  hiddenTags: string[];
}

interface CatalogActions {
  /**
   * 日付ボタン(new/old統合)向けのトグル対応セレクター。"new"/"old"を渡すと
   * 日付系(new/old)グループとして扱い、既に日付系が選択中ならnew⇔oldを反転する。
   * bump/replies/momentum/soudaneを渡した場合はそのsortをそのまま選択する
   * (Issue #136: 新順/古順ボタンの統合)。いずれもpageを1へ戻す。
   */
  selectSortMethod: (sort: SortType) => void;
  setPage: (page: number) => void;
  initializeLayout: (isDesktop: boolean) => void;
  setColumns: (columns: number) => void;
  setRows: (rows: number) => void;
  setTextLines: (lines: number) => void;
  setImageSize: (size: CatalogImageSize) => void;
  setOpenInNewTab: (open: boolean) => void;
  setShowNew: (show: boolean) => void;
  setShowCount: (show: boolean) => void;
  setShowUnreadCount: (show: boolean) => void;
  setCatalogAnim: (anim: AnimSetting) => void;
  setAutoReload: (enabled: boolean) => void;
  setCatalogHoverPreview: (enabled: boolean) => void;
  setCatalogHoverScale: (scale: number) => void;
  setCatalogFontScale: (scale: number) => void;
  setThreadMenuOpenMethod: (method: ThreadMenuOpenMethod) => void;
  updateLastCatalogIds: (
    context: CatalogContext,
    ids: readonly string[],
  ) => void;
  setSearchQuery: (query: string) => void;
  setSearchScope: (scope: SearchScope) => void;
  setSelectedTag: (tag: string | null) => void;
  addHiddenTag: (tag: string) => void;
  removeHiddenTag: (tag: string) => void;
  clearHiddenTags: () => void;
  resetCatalogSettings: () => void;
}

type CatalogState = CatalogValues & CatalogActions;
type PersistedCatalogValues = Omit<
  CatalogValues,
  "page" | "searchQuery" | "searchScope" | "selectedTag"
>;
export type CatalogStore = UseBoundStore<
  Mutate<StoreApi<CatalogState>, [["zustand/persist", unknown]]>
>;

export interface CatalogLayout {
  columns: number;
  rows: number;
}

interface CatalogLayoutInitialization extends CatalogLayout {
  isLayoutInitialized: true;
}

const DESKTOP_INITIAL_LAYOUT: CatalogLayout = {
  columns: 8,
  rows: 8,
};
const MOBILE_INITIAL_LAYOUT: CatalogLayout = {
  columns: 4,
  rows: 12,
};
const LEGACY_INITIAL_LAYOUT: CatalogLayout = {
  columns: 7,
  rows: 8,
};

const DEFAULT_CATALOG_VALUES: CatalogValues = {
  currentSort: "bump",
  sortDirection: "desc",
  page: 1,
  ...DESKTOP_INITIAL_LAYOUT,
  isLayoutInitialized: false,
  textLines: CATALOG_TEXT_LINES_DEFAULT,
  imageSize: 100,
  openInNewTab: false,
  showNew: true,
  showCount: true,
  showUnreadCount: true,
  catalogAnim: "always",
  autoReload: false,
  catalogHoverPreview: false,
  catalogHoverScale: CATALOG_HOVER_SCALE_DEFAULT,
  catalogFontScale: CATALOG_FONT_SCALE_DEFAULT,
  threadMenuOpenMethod: "auto",
  lastCatalogIds: {},
  searchQuery: "",
  searchScope: "all",
  selectedTag: null,
  hiddenTags: [],
};

export function getCatalogPageLimit(columns: number, rows: number): number {
  return Math.max(
    1,
    Math.min(CATALOG_PAGE_LIMIT_MAX, Math.floor(columns) * Math.floor(rows)),
  );
}

export function createCatalogContext(
  boardSlug: string,
  page: number,
  sort: SortType,
): CatalogContext {
  return {
    boardSlug,
    page: normalizeCatalogPage(page),
    sort,
  };
}

export function createCatalogContextKey(
  context: CatalogContext,
): CatalogContextKey {
  const normalized = createCatalogContext(
    context.boardSlug,
    context.page,
    context.sort,
  );
  return JSON.stringify([
    normalized.boardSlug,
    normalized.page,
    normalized.sort,
  ]) as CatalogContextKey;
}

/**
 * 保持する閲覧文脈の上限。文脈キーは 板 × ソート × ページ の組み合わせで、
 * ページ送りのたびに増える。上限がないと localStorage を食い潰し、
 * 保存失敗で他の設定まで永続化されなくなる。
 */
export const MAX_CATALOG_HISTORY_ENTRIES = 60;
/** 無限表示でも1文脈の保存量を従来のcatalog API最大1ページ分に収める。 */
export const MAX_CATALOG_HISTORY_IDS_PER_CONTEXT: number =
  CATALOG_PAGE_LIMIT_MAX;

/**
 * 参照の古い文脈から落として上限内に収める。
 *
 * オブジェクトの文字列キーは挿入順を保つ。既存キーも一度落として
 * 積み直すことで、末尾ほど新しい参照になる。
 */
export function appendCatalogHistory(
  history: CatalogHistory,
  key: CatalogContextKey,
  ids: string[],
): CatalogHistory {
  const next: CatalogHistory = {};
  for (const [existingKey, existingIds] of Object.entries(history)) {
    if (existingKey === key || !existingIds) continue;
    next[existingKey as CatalogContextKey] = existingIds.slice(
      0,
      MAX_CATALOG_HISTORY_IDS_PER_CONTEXT,
    );
  }
  next[key] = ids.slice(0, MAX_CATALOG_HISTORY_IDS_PER_CONTEXT);

  const keys = Object.keys(next);
  if (keys.length <= MAX_CATALOG_HISTORY_ENTRIES) return next;
  for (const staleKey of keys.slice(
    0,
    keys.length - MAX_CATALOG_HISTORY_ENTRIES,
  )) {
    delete next[staleKey as CatalogContextKey];
  }
  return next;
}

export function getLastCatalogIds(
  state: CatalogHistoryState,
  context: CatalogContext,
): readonly string[] | undefined {
  return state.lastCatalogIds[createCatalogContextKey(context)];
}

export function getNewCatalogThreadIds(
  currentThreadIds: readonly string[],
  lastCatalogIds: readonly string[] | undefined,
): Set<string> {
  if (lastCatalogIds === undefined) return new Set<string>();
  const previous = new Set(lastCatalogIds);
  return new Set(currentThreadIds.filter((id) => !previous.has(id)));
}

export function resolveInitialCatalogLayout(isDesktop: boolean): CatalogLayout {
  return isDesktop ? DESKTOP_INITIAL_LAYOUT : MOBILE_INITIAL_LAYOUT;
}

export function getCatalogLayoutInitialization(
  state: Pick<CatalogValues, "isLayoutInitialized">,
  isDesktop: boolean,
): CatalogLayoutInitialization | undefined {
  if (state.isLayoutInitialized) return undefined;
  return {
    ...resolveInitialCatalogLayout(isDesktop),
    isLayoutInitialized: true,
  };
}

export function createCatalogStore(storage?: StateStorage): CatalogStore {
  const persistStorage = resolveCatalogStateStorage(storage);
  const jsonStorage = persistStorage
    ? createJSONStorage(() => persistStorage)
    : undefined;

  return create<CatalogState>()(
    persist(
      (set, get) => ({
        ...DEFAULT_CATALOG_VALUES,
        selectSortMethod: (sort: SortType) =>
          set((state) => {
            const isDateFamily = sort === "new" || sort === "old";
            if (!isDateFamily) {
              return { currentSort: sort, sortDirection: "desc", page: 1 };
            }
            const nextSort: SortType =
              state.currentSort === "new" ? "old" : "new";
            return { currentSort: nextSort, sortDirection: "desc", page: 1 };
          }),
        setPage: (page: number) => set({ page: Math.max(1, Math.floor(page)) }),
        initializeLayout: (isDesktop: boolean) => {
          const initialization = getCatalogLayoutInitialization(
            get(),
            isDesktop,
          );
          if (!initialization) return;
          set({ ...initialization, page: 1 });
        },
        setColumns: (columns: number) =>
          set({
            columns: clampInteger(
              columns,
              CATALOG_LAYOUT_MIN,
              CATALOG_COLUMNS_MAX,
            ),
            isLayoutInitialized: true,
            page: 1,
          }),
        setRows: (rows: number) =>
          set({
            rows: clampInteger(rows, CATALOG_LAYOUT_MIN, CATALOG_ROWS_MAX),
            isLayoutInitialized: true,
            page: 1,
          }),
        setTextLines: (textLines: number) =>
          set({
            textLines: clampInteger(
              textLines,
              CATALOG_TEXT_LINES_MIN,
              CATALOG_TEXT_LINES_MAX,
            ),
          }),
        setImageSize: (imageSize: CatalogImageSize) => set({ imageSize }),
        setOpenInNewTab: (openInNewTab: boolean) => set({ openInNewTab }),
        setShowNew: (showNew: boolean) => set({ showNew }),
        setShowCount: (showCount: boolean) => set({ showCount }),
        setShowUnreadCount: (showUnreadCount: boolean) =>
          set({ showUnreadCount }),
        setCatalogAnim: (catalogAnim: AnimSetting) => set({ catalogAnim }),
        setAutoReload: (autoReload: boolean) => set({ autoReload }),
        setCatalogHoverPreview: (catalogHoverPreview: boolean) =>
          set({ catalogHoverPreview }),
        setCatalogHoverScale: (catalogHoverScale: number) =>
          set({
            catalogHoverScale: clampInteger(
              catalogHoverScale,
              CATALOG_HOVER_SCALE_MIN,
              CATALOG_HOVER_SCALE_MAX,
            ),
          }),
        setCatalogFontScale: (catalogFontScale: number) =>
          set({
            catalogFontScale: clampInteger(
              catalogFontScale,
              CATALOG_FONT_SCALE_MIN,
              CATALOG_FONT_SCALE_MAX,
            ),
          }),
        setThreadMenuOpenMethod: (threadMenuOpenMethod: ThreadMenuOpenMethod) =>
          set({ threadMenuOpenMethod }),
        updateLastCatalogIds: (
          context: CatalogContext,
          ids: readonly string[],
        ) =>
          set((state) => ({
            lastCatalogIds: appendCatalogHistory(
              state.lastCatalogIds,
              createCatalogContextKey(context),
              filterUuidThreadIds(ids),
            ),
          })),
        setSearchQuery: (searchQuery: string) => set({ searchQuery, page: 1 }),
        setSearchScope: (searchScope: SearchScope) => set({ searchScope }),
        setSelectedTag: (selectedTag: string | null) =>
          set({
            selectedTag:
              selectedTag !== null && isCatalogFilterTag(selectedTag)
                ? selectedTag
                : null,
            page: 1,
          }),
        addHiddenTag: (hiddenTag: string) =>
          set((state) =>
            isCatalogFilterTag(hiddenTag)
              ? {
                  hiddenTags: normalizeCatalogFilterTags([
                    ...state.hiddenTags,
                    hiddenTag,
                  ]),
                  selectedTag:
                    state.selectedTag === hiddenTag ? null : state.selectedTag,
                  page: 1,
                }
              : {},
          ),
        removeHiddenTag: (hiddenTag: string) =>
          set((state) => ({
            hiddenTags: state.hiddenTags.filter((tag) => tag !== hiddenTag),
            page: 1,
          })),
        clearHiddenTags: () => set({ hiddenTags: [], page: 1 }),
        resetCatalogSettings: () => set({ ...DEFAULT_CATALOG_VALUES }),
      }),
      {
        name: "nijiurachan-catalog-settings",
        version: CATALOG_STORE_VERSION,
        ...(jsonStorage ? { storage: jsonStorage } : {}),
        migrate: migrateCatalogState,
        partialize: partializeCatalogState,
        merge: mergeCatalogState,
      },
    ),
  );
}

export const useCatalogStore: CatalogStore = createCatalogStore();

export function partializeCatalogState(
  state: CatalogState,
): PersistedCatalogValues {
  return {
    currentSort: state.currentSort,
    sortDirection: state.sortDirection,
    columns: state.columns,
    rows: state.rows,
    isLayoutInitialized: state.isLayoutInitialized,
    textLines: state.textLines,
    imageSize: state.imageSize,
    openInNewTab: state.openInNewTab,
    showNew: state.showNew,
    showCount: state.showCount,
    showUnreadCount: state.showUnreadCount,
    catalogAnim: state.catalogAnim,
    autoReload: state.autoReload,
    catalogHoverPreview: state.catalogHoverPreview,
    catalogHoverScale: state.catalogHoverScale,
    catalogFontScale: state.catalogFontScale,
    threadMenuOpenMethod: state.threadMenuOpenMethod,
    lastCatalogIds: state.lastCatalogIds,
    hiddenTags: normalizeCatalogFilterTags(state.hiddenTags),
  };
}

export function mergeCatalogState(
  persisted: unknown,
  current: CatalogState,
): CatalogState {
  if (!isRecord(persisted)) return current;
  const sanitizedPersisted = omitRemovedCatalogSettings(persisted);
  const hasPersistedLayout =
    sanitizedPersisted.columns !== undefined ||
    sanitizedPersisted.rows !== undefined;
  return {
    ...current,
    ...sanitizedPersisted,
    isLayoutInitialized:
      sanitizedPersisted.isLayoutInitialized === true || hasPersistedLayout,
    hiddenTags: normalizePersistedHiddenTags(sanitizedPersisted.hiddenTags),
    lastCatalogIds: normalizePersistedCatalogHistory(
      sanitizedPersisted.lastCatalogIds,
    ),
    // setterを経由しない保存値も、設定UIと同じ行列範囲へ揃える。
    columns: clampInteger(
      sanitizedPersisted.columns ?? current.columns,
      CATALOG_LAYOUT_MIN,
      CATALOG_COLUMNS_MAX,
    ),
    rows: clampInteger(
      sanitizedPersisted.rows ?? current.rows,
      CATALOG_LAYOUT_MIN,
      CATALOG_ROWS_MAX,
    ),
    // localStorageの手編集や壊れた永続値で0/負数が紛れ込むと
    // resolveCatalogHoverImageWidthが非正の幅を返してしまうため、
    // migrate経由・同一バージョン直読み込みのどちらもここで丸める
    // (zustand/persistのmerge はバージョン一致・不一致の両経路で必ず通る)。
    catalogHoverScale: clampInteger(
      sanitizedPersisted.catalogHoverScale ?? current.catalogHoverScale,
      CATALOG_HOVER_SCALE_MIN,
      CATALOG_HOVER_SCALE_MAX,
    ),
    // 文字倍率も同じ理由で丸める。0や負数がCSSのcalc()へ流れると
    // カタログの文字が消える。
    catalogFontScale: clampInteger(
      sanitizedPersisted.catalogFontScale ?? current.catalogFontScale,
      CATALOG_FONT_SCALE_MIN,
      CATALOG_FONT_SCALE_MAX,
    ),
    textLines: clampInteger(
      sanitizedPersisted.textLines ?? current.textLines,
      CATALOG_TEXT_LINES_MIN,
      CATALOG_TEXT_LINES_MAX,
    ),
  } as CatalogState;
}

export function migrateCatalogState(
  persisted: unknown,
  version: number,
): unknown {
  const migrated = migrateThreadIdArray(
    persisted,
    version,
    1,
    "lastCatalogIds",
  );
  if (!isRecord(migrated)) return migrated;
  const sanitizedMigrated = omitRemovedCatalogSettings(migrated);

  const direction = sanitizedMigrated.sortDirection === "asc" ? "asc" : "desc";
  const currentSort = normalizePersistedSort(
    sanitizedMigrated.currentSort,
    direction,
  );
  const hiddenTags = normalizePersistedHiddenTags(sanitizedMigrated.hiddenTags);
  const lastCatalogIds =
    version < CATALOG_CONTEXT_HISTORY_VERSION
      ? {}
      : normalizePersistedCatalogHistory(sanitizedMigrated.lastCatalogIds);
  const columns =
    sanitizedMigrated.columns === undefined
      ? LEGACY_INITIAL_LAYOUT.columns
      : sanitizedMigrated.columns;
  const rows =
    sanitizedMigrated.rows === undefined
      ? LEGACY_INITIAL_LAYOUT.rows
      : sanitizedMigrated.rows;
  return {
    ...sanitizedMigrated,
    currentSort,
    sortDirection: "desc",
    page: 1,
    columns,
    rows,
    isLayoutInitialized: true,
    hiddenTags,
    lastCatalogIds,
    textLines: clampInteger(
      sanitizedMigrated.textLines ?? CATALOG_TEXT_LINES_DEFAULT,
      CATALOG_TEXT_LINES_MIN,
      CATALOG_TEXT_LINES_MAX,
    ),
  };
}

/** 廃止済み設定を永続値から除外する。 */
function omitRemovedCatalogSettings(
  value: Record<string, unknown>,
): Record<string, unknown> {
  const sanitized = { ...value };
  // 旧文字数設定は行数へ推測変換せず、新しい既定値へ移行する。
  delete sanitized.textLength;
  delete sanitized.textPosition;
  return sanitized;
}

function normalizePersistedCatalogHistory(value: unknown): CatalogHistory {
  if (!isRecord(value) || Array.isArray(value)) return {};
  // 上限導入前に肥大した保存値を読み込んだ場合も、ここで末尾側だけ残す。
  let normalized: CatalogHistory = {};
  for (const [key, ids] of Object.entries(value)) {
    const context = parseCatalogContextKey(key);
    if (!context) continue;
    normalized = appendCatalogHistory(
      normalized,
      createCatalogContextKey(context),
      filterUuidThreadIds(ids),
    );
  }
  return normalized;
}

function parseCatalogContextKey(value: string): CatalogContext | undefined {
  try {
    const parsed: unknown = JSON.parse(value);
    if (
      !Array.isArray(parsed) ||
      parsed.length !== 3 ||
      typeof parsed[0] !== "string" ||
      parsed[0].length === 0 ||
      typeof parsed[1] !== "number" ||
      normalizeCatalogPage(parsed[1]) !== parsed[1] ||
      !isSortType(parsed[2])
    ) {
      return undefined;
    }
    return createCatalogContext(parsed[0], parsed[1], parsed[2]);
  } catch {
    return undefined;
  }
}

function normalizePersistedHiddenTags(value: unknown): string[] {
  return normalizeCatalogFilterTags(value);
}

function normalizePersistedSort(
  value: unknown,
  direction: SortDirection,
): SortType {
  if (value === "date" || value === "created") {
    return direction === "asc" ? "old" : "new";
  }
  if (value === "sodane") return "soudane";
  if (value === "default" || value === "recent") return "bump";
  return CATALOG_SORTS.find((sort) => sort === value) ?? "bump";
}

function isSortType(value: unknown): value is SortType {
  return CATALOG_SORTS.some((sort) => sort === value);
}

function normalizeCatalogPage(page: number): number {
  return Number.isFinite(page) ? Math.max(1, Math.floor(page)) : 1;
}

function clampInteger(
  value: unknown,
  minimum: number,
  maximum: number,
): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return minimum;
  return Math.max(minimum, Math.min(maximum, Math.floor(value)));
}

function resolveCatalogStateStorage(
  storage: StateStorage | undefined,
): StateStorage | undefined {
  if (storage) return makeCatalogStateStorageSafe(storage);
  try {
    return globalThis.localStorage
      ? makeCatalogStateStorageSafe(globalThis.localStorage)
      : undefined;
  } catch {
    return undefined;
  }
}

function makeCatalogStateStorageSafe(storage: StateStorage): StateStorage {
  return {
    getItem(name: string): string | null | Promise<string | null> {
      try {
        const value = storage.getItem(name);
        return value instanceof Promise ? value.catch((): null => null) : value;
      } catch {
        return null;
      }
    },
    setItem(name: string, value: string): unknown {
      try {
        const result = storage.setItem(name, value);
        return result instanceof Promise
          ? result.catch((): void => undefined)
          : result;
      } catch {
        // Storage refusal must not prevent catalog browsing.
      }
    },
    removeItem(name: string): unknown {
      try {
        const result = storage.removeItem(name);
        return result instanceof Promise
          ? result.catch((): void => undefined)
          : result;
      } catch {
        // Storage refusal must not prevent catalog browsing.
      }
    },
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
